import { useMemo } from "react";
import {
  ArrowLeft, Plane, Plus, Pencil, Trash2, Hotel, Utensils,
} from "lucide-react";
import { Button } from "../ui/button";
import { DASH, DOT, tripDates } from "./format";
import TripCard from "./TripCard";

/**
 * One client: what is true of it, what every trip put together says, and then
 * the trips themselves.
 *
 * The summary in the middle is the reason for grouping at all. Three people who
 * went to the same place used to leave three unrelated records; here their
 * hotels sit in one list with who stayed where, which is what somebody going
 * next actually wants to read.
 */
export default function ClientDetail({
  client, trips, language, t,
  onBack, onAddTrip, onEdit, onDeleteClient, onEditTrip, onDeleteTrip,
}) {
  // Gathered per value rather than per trip: the question is "which hotels do
  // people use here", and the answer is a short list with names against it.
  const summary = useMemo(() => {
    const collect = (pick) => {
      const map = new Map();
      trips.forEach((trip) => {
        pick(trip).forEach((value) => {
          if (!value) return;
          const key = String(value).trim();
          if (!key) return;
          if (!map.has(key)) map.set(key, []);
          map.get(key).push(trip);
        });
      });
      return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
    };

    return {
      hotels: collect((tr) => tr.hotels || []),
      airports: collect((tr) => (tr.airport ? [tr.airport] : [])),
      restaurants: collect((tr) => (tr.restaurants || []).map((r) => r.name)),
    };
  }, [trips]);

  return (
    <div className="p-6" data-testid="trasferte-client-detail">
      <button
        type="button"
        onClick={onBack}
        data-testid="client-back"
        className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        {t("trasferte.backToTrips")}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-slate-900 break-words">{client.name}</h2>
          {client.city && (
            <p className="text-sm text-slate-500 break-words">{client.city}</p>
          )}
          {client.contact && (
            <p className="text-sm text-slate-400 break-all mt-0.5">{client.contact}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={onEdit} data-testid="client-edit-btn" className="border-slate-200">
            <Pencil className="w-3.5 h-3.5 mr-1.5" />
            {t("trasferte.editClient")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDeleteClient}
            data-testid="client-delete-btn"
            aria-label={t("trasferte.deleteClient")}
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
          <Button onClick={onAddTrip} data-testid="client-add-trip-btn" className="bg-indigo-700 hover:bg-indigo-600 text-white">
            <Plus className="w-4 h-4 mr-2" />
            {t("trasferte.addTrip")}
          </Button>
        </div>
      </div>

      {client.notes && (
        <div className="border border-slate-100 rounded-lg p-4 bg-slate-50/50 mb-5">
          <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-1.5">
            {t("trasferte.clientNotes")}
          </h3>
          <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{client.notes}</p>
        </div>
      )}

      {trips.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Plane className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">{t("trasferte.noVisits")}</p>
        </div>
      ) : (
        <>
          <div className="border border-slate-100 rounded-lg p-4 bg-slate-50/50 mb-6">
            <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-3">
              {t("trasferte.summary")}
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <SummaryColumn icon={Hotel} label={t("trasferte.hotelsUsed")} entries={summary.hotels} language={language} />
              <SummaryColumn icon={Plane} label={t("trasferte.airportsUsed")} entries={summary.airports} language={language} />
              <SummaryColumn icon={Utensils} label={t("trasferte.restaurants")} entries={summary.restaurants} language={language} />
            </div>
          </div>

          <div className="space-y-4">
            {trips.map((trip) => (
              <TripCard
                key={trip.id}
                trip={trip}
                t={t}
                language={language}
                onEdit={() => onEditTrip(trip)}
                onDelete={() => onDeleteTrip(trip)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** One kind of thing, with who used it and when underneath. */
function SummaryColumn({ icon: Icon, label, entries, language }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mb-2">
        <Icon className="w-3.5 h-3.5 shrink-0" />
        <span className="break-words">{label}</span>
      </div>
      {entries.length === 0 ? (
        <p className="text-xs text-slate-300">{DASH}</p>
      ) : (
        <ul className="space-y-1.5">
          {entries.map(([value, list]) => (
            <li key={value} className="min-w-0">
              <p className="text-sm text-slate-700 break-words">{value}</p>
              <p className="text-xs text-slate-400 break-words">
                {list
                  .map((tr) => {
                    const when = tripDates(tr, language);
                    const who = tr.created_by_name || "";
                    return when ? `${who} ${DOT} ${when}` : who;
                  })
                  .join(" / ")}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
